// Pure helpers for deriving "tours" from artifacts.
//
// There is NO tour database in scope. A "tour" is just a `tourTag` slug that
// artifacts carry, and a tour's membership is DERIVED by scanning ALL artifacts
// and grouping them by their `tourTags` (a running merge: for every artifact, for
// every slug in artifact.tourTags, add the artifact to that slug's group).
//
// Order WITHIN a group is a frontend-only nicety persisted in localStorage
// (see tourStore.ts), reconciled here against current membership.

import type { Artifact, QuoteSlide } from "./types";
import { getOrder, getQuoteSlides, slugsWithQuoteSlides } from "./tourStore";

/**
 * One slide in a tour's ordered sequence: either a PHOTO (an artifact, derived from
 * `tourTags` membership) or a QUOTE slide (owned by the tour record's `quoteSlides`).
 * `id` is the entry that appears in the tour's `order` (a ULID, or a "qs_*" id).
 */
export type TourSlide =
  | { kind: "photo"; id: string; artifact: Artifact }
  | { kind: "quote"; id: string; slide: QuoteSlide };

/** A derived tour: a tourTag slug + its ordered slides (photos + quote slides). */
export interface TourGroup {
  slug: string;
  slides: TourSlide[];
}

/** Newest-updated first — the stable tiebreak for not-yet-ordered photos. */
function byUpdatedDesc(a: Artifact, b: Artifact): number {
  return a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0;
}

/**
 * Sequence a tour's slides by the saved `order`, then append any members not yet in
 * it: photos newest-updated first, then leftover quote slides in array order. Saved
 * ids with no current member (an untagged artwork, a deleted quote slide) are dropped.
 * This is the single source of truth for how a tour's slides are sequenced, mixing
 * photos (from `members`) and quote slides (from `quoteSlides`) into one ordered list.
 */
export function orderTourSlides(
  slug: string,
  members: Artifact[],
  quoteSlides: QuoteSlide[],
): TourSlide[] {
  const saved = getOrder(slug);
  const photoById = new Map(members.map((m) => [m.id, m]));
  const quoteById = new Map(quoteSlides.map((q) => [q.id, q]));
  const seen = new Set<string>();
  const ordered: TourSlide[] = [];

  // 1. saved order, filtered to current members (photo OR quote slide)
  for (const id of saved) {
    if (seen.has(id)) continue;
    const photo = photoById.get(id);
    if (photo) {
      ordered.push({ kind: "photo", id, artifact: photo });
      seen.add(id);
      continue;
    }
    const q = quoteById.get(id);
    if (q) {
      ordered.push({ kind: "quote", id, slide: q });
      seen.add(id);
    }
  }
  // 2a. photos not yet ordered, newest-updated first
  const restPhotos = members.filter((m) => !seen.has(m.id));
  restPhotos.sort(byUpdatedDesc);
  for (const m of restPhotos) ordered.push({ kind: "photo", id: m.id, artifact: m });
  // 2b. quote slides not yet ordered, in array order
  for (const q of quoteSlides) {
    if (!seen.has(q.id)) ordered.push({ kind: "quote", id: q.id, slide: q });
  }

  return ordered;
}

/**
 * Group every artifact by its `tourTags` (the running merge), then fold in each
 * tour's quote slides. Returns one TourGroup per distinct slug, sorted by slug; the
 * slides within each group are sequenced by orderTourSlides() (saved order, then the
 * append rules). Slugs that have only quote slides (no tagged artworks yet) still
 * appear. Empty/blank slugs are ignored.
 */
export function groupByTour(artifacts: Artifact[]): TourGroup[] {
  const bySlug = new Map<string, Artifact[]>();
  for (const a of artifacts) {
    for (const raw of a.tourTags ?? []) {
      const slug = raw?.trim();
      if (!slug) continue;
      const list = bySlug.get(slug);
      if (list) list.push(a);
      else bySlug.set(slug, [a]);
    }
  }
  // A tour can exist with only quote slides (no tagged artworks) — surface it too.
  for (const slug of slugsWithQuoteSlides()) {
    if (!bySlug.has(slug)) bySlug.set(slug, []);
  }

  const groups: TourGroup[] = [];
  for (const [slug, members] of bySlug) {
    groups.push({
      slug,
      slides: orderTourSlides(slug, members, getQuoteSlides(slug)),
    });
  }
  groups.sort((x, y) => x.slug.localeCompare(y.slug));
  return groups;
}

/** The first PHOTO (artifact) slide in a sequence — for cover thumbnails. */
export function firstPhoto(slides: TourSlide[]): Artifact | undefined {
  for (const s of slides) if (s.kind === "photo") return s.artifact;
  return undefined;
}

/** Tally photos vs quote slides in a tour's slide list. */
export function slideCounts(slides: TourSlide[]): { photos: number; quotes: number } {
  let photos = 0;
  let quotes = 0;
  for (const s of slides) {
    if (s.kind === "photo") photos += 1;
    else quotes += 1;
  }
  return { photos, quotes };
}

/** A short, human label for a quote slide (its title, else attributions / first words). */
export function quoteSlideLabel(slide: QuoteSlide): string {
  if (slide.title && slide.title.trim()) return slide.title.trim();
  const attrs = slide.quotes
    .map((q) => q.attribution?.trim())
    .filter((x): x is string => !!x);
  if (attrs.length) return attrs.join(", ");
  const first = slide.quotes[0]?.text?.trim() ?? "";
  if (first) return first.length > 60 ? `${first.slice(0, 57)}…` : first;
  return "Quote";
}

/**
 * Artifacts NOT in the given tour — the candidate pool for the "add artworks"
 * picker. An artifact is a candidate iff its `tourTags` does not already contain
 * the slug. Returned newest-updated first.
 */
export function artifactsNotInTour(all: Artifact[], slug: string): Artifact[] {
  const candidates = all.filter((a) => !(a.tourTags ?? []).includes(slug));
  candidates.sort(byUpdatedDesc);
  return candidates;
}

/** Count of artifacts carrying NO tour tag at all (subtle "untagged" hint). */
export function untaggedCount(all: Artifact[]): number {
  let n = 0;
  for (const a of all) {
    const tags = (a.tourTags ?? []).filter((t) => t?.trim());
    if (tags.length === 0) n++;
  }
  return n;
}

/**
 * Friendly display title for a tour slug, e.g. "dutch-masters" -> "Dutch Masters".
 * Slugs are the operator's own free-text (slugified) labels, so this is a light
 * touch-up for headings — the raw slug is still what's stored on artifacts.
 */
export function tourTitle(slug: string): string {
  return slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** A short, human label for one artifact in a tour grid. */
export function artifactLabel(a: Artifact): string {
  if (a.title && a.title.trim()) return a.title.trim();
  if (a.artist && a.artist.trim()) return a.artist.trim();
  if (a.notes && a.notes.trim()) return a.notes.trim();
  return "Untitled";
}
